<script setup lang="tsx">
import { useI18n } from 'vue-i18n'
import { Tabs } from '@/types/risk-management'
import { computed } from 'vue'
import { hasPermission } from '@/services/utils'
import { PermAdminRiskSettings } from '@/models/enums-generated'

const props = defineProps<{
	modelValue: Tabs
}>()
const activeTab = computed({
	get: () => props.modelValue,
	set: (val) => emit('update:modelValue', val),
})

const emit = defineEmits(['update:modelValue'])
defineExpose({ activeTab })

const { t } = useI18n()
const canSeeSettings = computed(() => hasPermission(PermAdminRiskSettings))

const tabs = [
	{
		id: 'risk-management-tab',
		label: t('risk.tabs.riskManagement'),
		name: Tabs.Risks,
		paneId: 'risk-management-tab-pane',
	},
	{
		id: 'threats-tab',
		label: t('risk.tabs.threats'),
		name: Tabs.Threats,
		paneId: 'threats-tab-pane',
	},
	{
		id: 'vulnerabilities-tab',
		label: t('risk.tabs.vulnerabilities'),
		name: Tabs.Vulnerabilities,
		paneId: 'vulnerabilities-tab-pane',
	},
	{
		id: 'risk-matrix-tab',
		label: t('risk.tabs.riskSettings'),
		name: Tabs.RiskSettings,
		paneId: 'risk-matrix-tab-pane',
	},
]
</script>

<template>
	<ul class="nav nav-tabs mb-3" id="risk-tabs" role="tablist">
		<li v-for="t in tabs" :key="t.id" class="nav-item" role="presentation">
			<button
				v-if="t.name !== Tabs.RiskSettings || canSeeSettings"
				:class="['nav-link', { active: activeTab === t.name }]"
				:data-test="t.id"
				type="button"
				role="tab"
				:aria-controls="t.paneId"
				@click="activeTab = t.name"
			>
				{{ t.label }}
			</button>
		</li>
	</ul>

	<div class="tab-content" id="tabs-content">
		<div
			v-for="t in tabs"
			:key="t.paneId"
			class="tab-pane fade"
			:class="{ show: activeTab === t.name, active: activeTab === t.name }"
			:id="t.paneId"
			role="tabpanel"
			:aria-labelledby="t.id"
			tabindex="0"
		>
			<template v-if="t.name !== Tabs.RiskSettings || canSeeSettings"><slot :name="t.name" /></template>
		</div>
	</div>
</template>
